import moment from "moment-timezone";
import { DAYS_OF_WEEK } from "./constants";

export function inclusiveDateToStart(dateStr, timezoneName) {
  return moment.tz(dateStr, "YYYY-MM-DD", timezoneName || "UTC").startOf("day").toISOString();
}

export function inclusiveDateToExclusiveEnd(dateStr, timezoneName) {
  return moment
    .tz(dateStr, "YYYY-MM-DD", timezoneName || "UTC")
    .add(1, "day")
    .startOf("day")
    .toISOString();
}

export function exclusiveEndToInclusiveDate(iso, timezoneName) {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").subtract(1, "millisecond").format("YYYY-MM-DD");
}

export function instantToLocalDate(iso, timezoneName) {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").format("YYYY-MM-DD");
}

export function instantToLocalTime(iso, timezoneName) {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").format("HH:mm");
}

export function localDateTimeToIso(dateStr, timeStr, timezoneName) {
  return moment
    .tz(`${dateStr} ${timeStr || "00:00"}`, "YYYY-MM-DD HH:mm", timezoneName || "UTC")
    .toISOString();
}

export function localTimeCandidates(date, time, timezoneName) {
  const wallTime = `${date} ${time}`;
  const parsed = moment.utc(wallTime, "YYYY-MM-DD HH:mm", true);
  const zone = moment.tz.zone(timezoneName);
  if (!parsed.isValid() || !zone) return [];
  return [...new Set(zone.offsets)].map((offset) => parsed.valueOf() + offset * 60000)
    .filter((instant) => moment.tz(instant, timezoneName).format("YYYY-MM-DD HH:mm:ss") === `${wallTime}:00`)
    .sort((a, b) => a - b).map((instant) => new Date(instant).toISOString());
}

export function zonedRangeToInstants(values, timezoneName) {
  const resolve = (date, time, preferred, label) => {
    const candidates = localTimeCandidates(date, time, timezoneName);
    if (!candidates.length) throw new Error(`${label} is invalid or skipped by daylight saving. Choose another time.`);
    const preserved = preferred && candidates.find((candidate) => moment(candidate).isSame(preferred));
    if (preserved) return preserved;
    if (candidates.length > 1) throw new Error(`Choose which ${label.toLowerCase()} to use; the clock repeats this time.`);
    return candidates[0];
  };
  const startsAt = resolve(values.localDate, values.startTime, values.startInstant, "Start time");
  const endsAt = resolve(values.endDate || values.localDate, values.endTime, values.endInstant, "End time");
  if (moment(endsAt).isSameOrBefore(startsAt)) throw new Error("End must be after start. Check the end date for overnight hours.");
  return { startsAt, endsAt };
}

export function instantRangeToLocal(startsAt, endsAt, timezoneName) {
  return {
    localDate: instantToLocalDate(startsAt, timezoneName),
    endDate: instantToLocalDate(endsAt, timezoneName),
    startTime: instantToLocalTime(startsAt, timezoneName),
    endTime: instantToLocalTime(endsAt, timezoneName),
    startInstant: startsAt,
    endInstant: endsAt,
  };
}

export function formatInZone(iso, timezoneName, pattern = "YYYY-MM-DD HH:mm") {
  if (!iso) return "";
  return moment.tz(iso, timezoneName || "UTC").format(pattern);
}

export function formatRange(startsAt, endsAt, timezoneName) {
  const sameDay = instantToLocalDate(startsAt, timezoneName) === instantToLocalDate(endsAt, timezoneName);
  return `${formatInZone(startsAt, timezoneName, "MMM D, YYYY h:mm A")} – ${formatInZone(
    endsAt,
    timezoneName,
    sameDay ? "h:mm A" : "MMM D, YYYY h:mm A"
  )}`;
}

export function weekdayFromLocalDate(dateStr) {
  const isoWeekday = moment.utc(dateStr, "YYYY-MM-DD").isoWeekday();
  return DAYS_OF_WEEK[isoWeekday - 1]?.value;
}

export function daysBetween(fromDate, toDate) {
  return moment.utc(toDate, "YYYY-MM-DD").diff(moment.utc(fromDate, "YYYY-MM-DD"), "days");
}

export function cycleWeekIndex(cycleAnchorDate, localDate, cycleWeeks) {
  const weeks = Number(cycleWeeks) || 1;
  const delta = daysBetween(cycleAnchorDate, localDate);
  return ((Math.floor(delta / 7) % weeks) + weeks) % weeks;
}

export function timeToMinutes(time) {
  const [hours, minutes] = String(time || "00:00")
    .slice(0, 5)
    .split(":")
    .map(Number);
  return hours * 60 + minutes;
}

export function isOvernightWindow(startTime, endTime) {
  return timeToMinutes(endTime) <= timeToMinutes(startTime);
}

function categoryKey(ids) {
  return [...(ids || [])].slice().sort().join(",");
}

function shiftGroupKey(window) {
  return [
    Number(window.weekIndex || 0),
    window.dayOfWeek,
    window.mode || "regular",
    categoryKey(window.serviceCategoryIds),
    window.disambiguation || "reject",
  ].join("|");
}

const DAY_ORDER = Object.fromEntries(DAYS_OF_WEEK.map((day, index) => [day.value, index]));

function compareWindows(left, right) {
  return (
    Number(left.weekIndex || 0) - Number(right.weekIndex || 0) ||
    (DAY_ORDER[left.dayOfWeek] ?? 0) - (DAY_ORDER[right.dayOfWeek] ?? 0) ||
    (left.mode || "").localeCompare(right.mode || "") ||
    categoryKey(left.serviceCategoryIds).localeCompare(categoryKey(right.serviceCategoryIds)) ||
    (left.disambiguation || "reject").localeCompare(right.disambiguation || "reject") ||
    (left.startDayOffset || 0) - (right.startDayOffset || 0) ||
    String(left.startTime || "").localeCompare(String(right.startTime || ""))
  );
}

function toFormWindow(window, breaks = []) {
  return {
    ...(window.disambiguation !== undefined ? { disambiguation: window.disambiguation } : {}),
    ...(window.startDayOffset ? { startDayOffset: window.startDayOffset } : {}),
    weekIndex: Number(window.weekIndex || 0),
    dayOfWeek: window.dayOfWeek,
    startTime: window.startTime,
    endTime: window.endTime,
    mode: window.mode || "regular",
    serviceCategoryIds: window.serviceCategoryIds || [],
    breaks,
  };
}

/** Merge same-day bookable windows into one shift plus lunch/break gaps. */
export function collapseWindowsToShifts(windows = []) {
  const sorted = [...windows].sort(compareWindows);
  const groups = [];
  sorted.forEach((window) => {
    const last = groups[groups.length - 1];
    if (last && shiftGroupKey(last[0]) === shiftGroupKey(window)) {
      last.push(window);
    } else {
      groups.push([window]);
    }
  });

  const shifts = groups.flatMap((group) => {
    if (group.some(window => window.startDayOffset === 1)) {
      const sameDay = group.filter(window => !window.startDayOffset).sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
      const nextDay = group.filter(window => window.startDayOffset === 1).sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));
      const first = sameDay.pop();
      if (!first) return nextDay.map(window => toFormWindow(window));
      const night = [first, ...nextDay];
      const bounds = night.map(window => ({ start: timeToMinutes(window.startTime) + (window.startDayOffset || 0) * 1440,
        end: timeToMinutes(window.endTime) + ((window.startDayOffset || 0) + (isOvernightWindow(window.startTime, window.endTime) ? 1 : 0)) * 1440 }));
      if (bounds[bounds.length - 1].end - bounds[0].start > 1440 || bounds.some((value, index) => index && value.start < bounds[index - 1].end)) {
        return group.map(window => toFormWindow(window));
      }
      const breaks = night.slice(1).flatMap((window, index) => bounds[index].end < bounds[index + 1].start
        ? [{ startTime: night[index].endTime, endTime: window.startTime }] : []);
      return [...collapseWindowsToShifts(sameDay), toFormWindow({ ...first, endTime: night[night.length - 1].endTime }, breaks)];
    }
    if (group.some((window) => isOvernightWindow(window.startTime, window.endTime))) {
      return group.map((window) => toFormWindow(window));
    }
    const ordered = [...group].sort((left, right) =>
      String(left.startTime).localeCompare(String(right.startTime))
    );
    const overlaps = ordered.some(
      (window, index) =>
        index > 0 && timeToMinutes(window.startTime) < timeToMinutes(ordered[index - 1].endTime)
    );
    if (overlaps) {
      return group.map((window) => toFormWindow(window));
    }
    const breaks = [];
    for (let index = 1; index < ordered.length; index += 1) {
      if (timeToMinutes(ordered[index].startTime) > timeToMinutes(ordered[index - 1].endTime)) {
        breaks.push({
          startTime: ordered[index - 1].endTime,
          endTime: ordered[index].startTime,
        });
      }
    }
    return [
      toFormWindow(
        {
          ...ordered[0],
          startTime: ordered[0].startTime,
          endTime: ordered[ordered.length - 1].endTime,
        },
        breaks
      ),
    ];
  });

  return shifts.sort((left, right) =>
    left.weekIndex - right.weekIndex ||
    (DAY_ORDER[left.dayOfWeek] ?? 0) - (DAY_ORDER[right.dayOfWeek] ?? 0) ||
    (left.startDayOffset || 0) - (right.startDayOffset || 0) ||
    timeToMinutes(left.startTime) - timeToMinutes(right.startTime)
  );
}

/** Position breaks on the shift's local timeline, including the following day. */
export function normalizeShiftBreaks(shift) {
  const breaks = (shift.breaks || []).filter((item) => item.startTime && item.endTime);
  if (!breaks.length) {
    return { error: null, breaks: [] };
  }
  const dayOffset = (shift.startDayOffset || 0) * 1440;
  const shiftStart = timeToMinutes(shift.startTime) + dayOffset;
  const shiftEnd = timeToMinutes(shift.endTime) + dayOffset + (isOvernightWindow(shift.startTime, shift.endTime) ? 1440 : 0);
  const normalized = breaks
    .map((item) => {
      const startClock = timeToMinutes(item.startTime), endClock = timeToMinutes(item.endTime);
      const start = startClock + dayOffset + (startClock < timeToMinutes(shift.startTime) ? 1440 : 0);
      const end = endClock + Math.floor(start / 1440) * 1440 + (endClock < startClock ? 1440 : 0);
      return { ...item, start, end };
    })
    .sort((left, right) => left.start - right.start);

  for (const item of normalized) {
    if (item.end <= item.start) {
      return { error: "Each lunch or break must end after it starts." };
    }
    if (item.start < shiftStart || item.end > shiftEnd) {
      return { error: "Lunch and breaks must fall inside that day's start and end times." };
    }
  }
  for (let index = 1; index < normalized.length; index += 1) {
    if (normalized[index].start < normalized[index - 1].end) {
      return { error: "Lunch and breaks on the same day cannot overlap." };
    }
  }
  return { error: null, intervals: normalized, breaks: normalized.map(({ start, end, ...pause }) => pause) };
}

/** Turn dated breaks into calendar-block instants. Hours stay intact. */
export function shiftBreaksToBlockRanges(shift, localDate, timezoneName) {
  const expectedEndDate = moment.utc(localDate).add((shift.startDayOffset || 0) + (isOvernightWindow(shift.startTime, shift.endTime) ? 1 : 0), "days").format("YYYY-MM-DD");
  if ((shift.breaks || []).some(item => item.startTime && item.endTime) && shift.endDate && shift.endDate !== expectedEndDate) {
    return { error: "Add breaks to a single shift, ending the same day or the following morning.", ranges: [] };
  }
  const validated = normalizeShiftBreaks(shift);
  if (validated.error) {
    return { error: validated.error, ranges: [] };
  }
  if (!validated.breaks.length) return { error: null, ranges: [] };
  try {
    const ranges = validated.intervals.map((pause) => {
        const startDate = moment.utc(localDate).add(Math.floor(pause.start / 1440), "days").format("YYYY-MM-DD");
        const endDate = moment.utc(localDate).add(Math.floor(pause.end / 1440), "days").format("YYYY-MM-DD");
        const pick = (date, time) => {
          const candidates = localTimeCandidates(date, time, timezoneName);
          return pause.disambiguation === "earlier" ? candidates[0] : pause.disambiguation === "later" ? candidates[candidates.length - 1] : undefined;
        };
        return zonedRangeToInstants({
            localDate: startDate,
            endDate,
            startTime: pause.startTime,
            endTime: pause.endTime,
            startInstant: pick(startDate, pause.startTime),
            endInstant: pick(endDate, pause.endTime),
          },
          timezoneName
        );
      });
    const startDate = moment.utc(localDate).add(shift.startDayOffset || 0, "days").format("YYYY-MM-DD");
    const hours = zonedRangeToInstants({ localDate: startDate, endDate: expectedEndDate,
      startTime: shift.startTime, endTime: shift.endTime,
      startInstant: shift.startInstant || resolvePreviewTime(startDate, shift.startTime, timezoneName, shift.disambiguation).instant,
      endInstant: shift.endInstant || resolvePreviewTime(expectedEndDate, shift.endTime, timezoneName, shift.disambiguation).instant,
    }, timezoneName);
    if (ranges.some(range => range.startsAt < hours.startsAt || range.endsAt > hours.endsAt)) {
      return { error: "Breaks must fall inside the selected working hours, including the chosen repeated hour.", ranges: [] };
    }
    return { error: null, ranges };
  } catch (error) {
    return { error: error.message, ranges: [] };
  }
}

/** Punch lunch/break gaps out of a shift so the API still receives bookable windows. */
export function expandShiftsToWindows(shifts = []) {
  const windows = [];
  for (const shift of shifts) {
    const result = expandShift(shift);
    if (result.error) {
      return { error: result.error, windows: [] };
    }
    windows.push(...result.windows);
  }
  return { error: null, windows };
}

function expandShift(shift) {
  const base = {
    ...(shift.disambiguation !== undefined ? { disambiguation: shift.disambiguation } : {}),
    ...(shift.startDayOffset ? { startDayOffset: shift.startDayOffset } : {}),
    weekIndex: Number(shift.weekIndex || 0),
    dayOfWeek: shift.dayOfWeek,
    mode: shift.mode || "regular",
    serviceCategoryIds: shift.serviceCategoryIds,
  };
  const validated = normalizeShiftBreaks(shift);
  if (validated.error) {
    return { error: validated.error };
  }
  if (!validated.breaks.length) {
    return {
      windows: [{ ...base, startTime: shift.startTime, endTime: shift.endTime }],
    };
  }

  const shiftStart = timeToMinutes(shift.startTime) + (shift.startDayOffset || 0) * 1440;
  const shiftEnd = timeToMinutes(shift.endTime) + ((shift.startDayOffset || 0) + (isOvernightWindow(shift.startTime, shift.endTime) ? 1 : 0)) * 1440;
  const segments = [];
  let cursor = shiftStart;
  validated.intervals.forEach((item) => {
    if (item.start > cursor) {
      segments.push({ start: cursor, end: item.start });
    }
    cursor = item.end;
  });
  if (cursor < shiftEnd) {
    segments.push({ start: cursor, end: shiftEnd });
  }
  if (!segments.length) {
    return { error: "A day needs bookable hours after lunch and breaks." };
  }
  const clock = value => `${String(Math.floor((value % 1440) / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
  return { windows: segments.map(segment => ({ ...base,
    ...(segment.start >= 1440 ? { startDayOffset: 1 } : {}),
    startTime: clock(segment.start), endTime: clock(segment.end) })) };
}

// Match the backend's explicit clock-change policy without Moment's automatic
// gap shifting or its mutable global parsing flags.
function resolvePreviewTime(date, time, timezoneName, policy = "reject") {
  const wall = moment.utc(`${date} ${time}`, "YYYY-MM-DD HH:mm", true);
  if (!wall.isValid()) return {};
  const candidates = localTimeCandidates(date, time, timezoneName);
  if (candidates.length === 1) return { instant: candidates[0] };
  const issue = { localTime: `${date}T${time}:00`, reason: candidates.length ? "ambiguous" : "nonexistent" };
  if (policy === "reject") return { issue };
  if (candidates.length > 1) {
    return { instant: policy === "later" ? candidates[candidates.length - 1] : candidates[0] };
  }
  const zone = moment.tz.zone(timezoneName);
  for (let index = 0; index < zone.untils.length - 1; index++) {
    const transition = zone.untils[index];
    const before = Math.round(zone.offsets[index] * 60000);
    const after = Math.round(zone.offsets[index + 1] * 60000);
    if (wall.valueOf() >= transition - before && wall.valueOf() < transition - after) {
      return { instant: new Date(wall.valueOf() + (policy === "earlier" ? after : before)).toISOString() };
    }
  }
  return { issue };
}

export function previewRuleEvents({
  weeklyWindows = [],
  cycleWeeks = 1,
  cycleAnchorDate,
  weekStart,
  rangeStart,
  rangeEnd,
  effectiveFrom,
  effectiveTo,
  timezoneName,
  onOccurrenceIssue,
}) {
  const start = rangeStart
    ? moment.tz(rangeStart, timezoneName || "UTC").startOf("day")
    : moment.tz(weekStart, timezoneName || "UTC").startOf("isoWeek");
  const end = rangeEnd
    ? moment.tz(rangeEnd, timezoneName || "UTC").startOf("day")
    : start.clone().add(7, "days");
  const events = [];
  for (let day = start.clone().subtract(1, "day"); day.isBefore(end); day = day.clone().add(1, "day")) {
    const localDate = day.format("YYYY-MM-DD");
    if (effectiveFrom && localDate < effectiveFrom) continue;
    if (effectiveTo && localDate > effectiveTo) continue;
    const weekIndex = cycleWeekIndex(cycleAnchorDate || localDate, localDate, cycleWeeks);
    const dayOfWeek = weekdayFromLocalDate(localDate);
    const dateStart = events.length;
    const windows = weeklyWindows.filter((window) => Number(window.weekIndex || 0) === weekIndex && window.dayOfWeek === dayOfWeek);
    for (const [idx, window] of windows.entries()) {
      const zone = timezoneName || "UTC";
      // Overnight is determined by the requested wall times, before resolving
      // offsets. A DST gap must never turn a same-day shift into an overnight one.
      const startDate = day.clone().add(window.startDayOffset || 0, "days").format("YYYY-MM-DD");
      const endDate = day.clone().add((window.startDayOffset || 0) + (window.endTime <= window.startTime ? 1 : 0), "days").format("YYYY-MM-DD");
      const resolvedStart = resolvePreviewTime(startDate, window.startTime, zone, window.disambiguation);
      const resolvedEnd = resolvePreviewTime(endDate, window.endTime, zone, window.disambiguation);
      let issue = resolvedStart.issue || resolvedEnd.issue;
      if (!issue && resolvedStart.instant && resolvedEnd.instant <= resolvedStart.instant) {
        issue = resolvePreviewTime(startDate, window.startTime, zone).issue || resolvePreviewTime(endDate, window.endTime, zone).issue;
      }
      if (issue) {
        events.splice(dateStart);
        // Include a previous-day overnight shift only if it reaches this view.
        if (localDate >= start.format("YYYY-MM-DD") || windows.some(item => item.startDayOffset || item.endTime <= item.startTime)) {
          onOccurrenceIssue?.({ localDate, ...issue });
        }
        break;
      }
      if (!resolvedStart.instant || !resolvedEnd.instant || resolvedEnd.instant <= resolvedStart.instant) continue;
      const startAt = moment(resolvedStart.instant);
      const endAt = moment(resolvedEnd.instant);
      if (!endAt.isAfter(start) || !startAt.isBefore(end)) continue;
      events.push({
        id: `preview-${localDate}-${idx}`,
        title: `${window.startTime}–${window.endTime}`,
        start: startAt.toISOString(),
        end: endAt.toISOString(),
      });
    }
  }
  return events;
}
